import { Button, Dialog, DialogContent } from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { personalChannelsQuery } from '../settings/channels-api';
import {
  currentEndpoint,
  dismissPrompt,
  promptDismissed,
  pushBlocked,
  pushSupport,
  standalone,
} from '../settings/push';
import { usePushSwitch } from '../settings/push-switch';

/**
 * Asks for push right after the app was installed to the home screen (ADR-0059). iPhone and iPad
 * show the question of the system only after a tap, so the app asks first and the button starts
 * it. Comes again after a sign-out on the device, never after "Später" or after push was switched
 * off here.
 */
export function PushPrompt() {
  const { t } = useTranslation();
  const installed = standalone() && pushSupport() === 'supported';
  const { data } = useQuery({ ...personalChannelsQuery, enabled: installed });
  const { switchOn, switching } = usePushSwitch();
  const [registered, setRegistered] = useState<boolean | undefined>(undefined);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!data) return;
    let current = true;
    void currentEndpoint()
      .catch(() => null)
      .then((endpoint) => {
        const known = !!endpoint && data.push.devices.some((d) => d.endpoint === endpoint);
        if (current) setRegistered(known);
      });
    return () => {
      current = false;
    };
  }, [data]);

  const publicKey = data?.push.available ? data.push.publicKey : null;
  const open =
    installed &&
    !!publicKey &&
    registered === false &&
    !closed &&
    !pushBlocked() &&
    !promptDismissed();
  if (!open) return null;

  const later = () => {
    dismissPrompt();
    setClosed(true);
  };

  return (
    <Dialog open onOpenChange={(next) => !next && later()}>
      <DialogContent
        title={t('shell.pushPrompt.title')}
        description={t('shell.pushPrompt.description')}
        footer={
          <>
            <Button variant="ghost" onClick={later}>
              {t('shell.pushPrompt.later')}
            </Button>
            <Button
              variant="primary"
              icon="bell"
              loading={switching}
              onClick={() => {
                void switchOn(publicKey).then(() => setClosed(true));
              }}
            >
              {t('shell.pushPrompt.allow')}
            </Button>
          </>
        }
      >
        <p className="text-fg-muted">{t('shell.pushPrompt.hint')}</p>
      </DialogContent>
    </Dialog>
  );
}
